import type { PercentCrop } from 'react-image-crop';

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.crossOrigin = 'anonymous';
    image.src = url;
  });
}

function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = getRadianAngle(rotation);
  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * Apply crop, rotation, flip, and optional CSS filter to an image and return as a Blob.
 *
 * `percentCrop` comes from react-image-crop (percentage-based, 0-100).
 * We convert to natural pixel coordinates, then apply rotation/flip/filter via canvas.
 */
export async function getCroppedImage(
  imageSrc: string,
  percentCrop: PercentCrop,
  rotation: number = 0,
  flipH: boolean = false,
  flipV: boolean = false,
  filterString: string = 'none'
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const natW = image.naturalWidth;
  const natH = image.naturalHeight;

  // Convert percent crop to natural pixel coordinates
  const cropX = (percentCrop.x / 100) * natW;
  const cropY = (percentCrop.y / 100) * natH;
  const cropW = (percentCrop.width / 100) * natW;
  const cropH = (percentCrop.height / 100) * natH;

  // Step 1: Extract the cropped region at natural resolution
  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = cropW;
  cropCanvas.height = cropH;
  const cropCtx = cropCanvas.getContext('2d');
  if (!cropCtx) throw new Error('Could not get canvas context');

  cropCtx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

  // Step 2: Apply rotation + flip to the cropped region
  const { width: bBoxW, height: bBoxH } = rotateSize(cropW, cropH, rotation);
  const outCanvas = document.createElement('canvas');
  outCanvas.width = bBoxW;
  outCanvas.height = bBoxH;
  const outCtx = outCanvas.getContext('2d');
  if (!outCtx) throw new Error('Could not get canvas context');

  // Apply filter to the final output
  if (filterString && filterString !== 'none') {
    outCtx.filter = filterString;
  }

  outCtx.translate(bBoxW / 2, bBoxH / 2);
  outCtx.rotate(getRadianAngle(rotation));
  outCtx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
  outCtx.translate(-cropW / 2, -cropH / 2);
  outCtx.drawImage(cropCanvas, 0, 0);

  return new Promise((resolve, reject) => {
    outCanvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob'));
      },
      'image/jpeg',
      0.9
    );
  });
}
